import React, { ReactNode, useState } from 'react';
import { LuSettings, LuX } from 'react-icons/lu';

import { PersonOrgFinancialProfile, PersonOrgType } from '@ndvr/customer-api';

import { CopyGuidDropdownMenuItem } from '@/components/CopyGuidComponents';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { EllipsisDropdownMenuTrigger } from '@/components/ui/dropdown-menu-triggers';
import { useRequiredParams } from 'utils';
import { ConfirmModal, ModalText } from 'widgets';

import { useUnlinkPersonFromHousehold } from '../household-queries';
import { LinkPersonDrawer } from '../LinkPersonToHouseholdDrawer';

/**
 * Shared link/unlink wiring for the household member lists: owns the link-drawer and
 * unlink-confirm state and renders both, parameterized by the list's PersonOrg type and copy.
 */
export const useMemberLinkActions = ({
    personOrgType,
    unlinkNoun,
    getDisplayName,
    unlinkNote,
}: {
    personOrgType: PersonOrgType;
    unlinkNoun: string;
    getDisplayName: (profile: PersonOrgFinancialProfile) => string;
    unlinkNote?: (profile: PersonOrgFinancialProfile) => ReactNode;
}) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { mutate: unlinkPersonFromHousehold } = useUnlinkPersonFromHousehold();
    const [showLinkDrawer, setShowLinkDrawer] = useState(false);
    const [profileToUnlink, setProfileToUnlink] = useState<PersonOrgFinancialProfile | undefined>(undefined);

    const linkUnlinkNodes = (
        <>
            {showLinkDrawer && (
                <LinkPersonDrawer onClose={() => setShowLinkDrawer(false)} personOrgType={personOrgType} />
            )}
            {profileToUnlink && (
                <ConfirmModal
                    modalTitle={getDisplayName(profileToUnlink)}
                    onConfirm={() =>
                        unlinkPersonFromHousehold({
                            globalAccountGUID,
                            personOrgGUID: profileToUnlink.profile.personOrgGUID,
                        })
                    }
                    buttonProps={{ buttonType: 'destructive', children: `Unlink ${unlinkNoun}` }}
                    onClose={() => setProfileToUnlink(undefined)}
                >
                    {unlinkNote?.(profileToUnlink)}
                    <ModalText>Are you sure you want to unlink this {unlinkNoun}?</ModalText>
                </ConfirmModal>
            )}
        </>
    );

    return {
        openLinkDrawer: () => setShowLinkDrawer(true),
        requestUnlink: setProfileToUnlink,
        linkUnlinkNodes,
    };
};

export const MemberActionsCell = ({
    profile,
    onEdit,
    onUnlink,
    unlinkDisabled = false,
    extraMenuItems,
}: {
    profile: PersonOrgFinancialProfile;
    onEdit: (profile: PersonOrgFinancialProfile) => void;
    onUnlink: (profile: PersonOrgFinancialProfile) => void;
    unlinkDisabled?: boolean;
    extraMenuItems?: ReactNode;
}) => (
    <div
        className="flex flex-col items-end"
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
    >
        <DropdownMenu>
            <EllipsisDropdownMenuTrigger />
            <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => onEdit(profile)} className="flex w-full items-center gap-2">
                    <LuSettings className="h-4 w-4" />
                    Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {extraMenuItems}
                <DropdownMenuItem
                    onSelect={() => onUnlink(profile)}
                    disabled={unlinkDisabled}
                    className="flex w-full items-center gap-2"
                >
                    <LuX className="h-4 w-4" />
                    Unlink
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <CopyGuidDropdownMenuItem guid={profile.profile.personOrgGUID} />
            </DropdownMenuContent>
        </DropdownMenu>
    </div>
);
